import { MONEY_SCALE, type PiDetailDto, describe } from "@hisense/shared";
import { printHtml } from "./printWindow";
import { piDocumentFacts } from "./piDocument";

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

const na = (s: string | null | undefined, empty = "N/A") => {
  const v = (s ?? "").trim();
  return v ? esc(v) : empty;
};

const printDate = (ms: number | null | undefined) => {
  if (!ms) return "N/A";
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const major4 = (minor: number) => (minor / MONEY_SCALE).toFixed(4);
const major2 = (minor: number) => (minor / MONEY_SCALE).toFixed(2);

/**
 * Commercial PI print — layout and labels match the legacy generatePrintContent
 * in ProformaInvoice.js (black-header table, EXW/DOD/Under-MOQ columns, bank
 * remittance block). Unit prices stay 4dp; document totals stay 2dp.
 */
export function buildPiHtml(pi: PiDetailDto) {
  const { hasDtd, hasExw, hasUnderMoq, hasStyle, hasSubRef, hasColor, hasSize, hasMoq,
    totalColSpan, lineSubtotal, base, discountPct, less, grand } = piDocumentFacts(pi);

  const priceHeader = "Unit Price";
  // Logo của CHÍNH pháp nhân thu tiền trên chứng từ này; chưa đặt logo riêng
  // thì lùi về logo Hisense đi kèm ứng dụng. Trước đây luôn cắm cứng tệp đó,
  // nên chứng từ do pháp nhân khác phát hành vẫn in ra logo Hisense.
  const logoSrc = pi.paymentBank?.logoUrl || `${window.location.origin}/brand/hisense-logo.webp`;
  const shipContact = pi.consigneeSnapshot || pi.shipTo;
  const bank = pi.paymentBank;

  const itemRows = pi.items
    .map((it, index) => {
      const productId = it.productCode || (it.productId ? it.productId.slice(-8) : "-");
      const unitShown = it.unitPrice;
      const zebra = index % 2 === 0 ? "white" : "#f5f5f5";
      return `<tr style="background-color:${zebra};">
        <td class="c mono b">${esc(productId)}</td>
        ${hasStyle ? `<td class="c">${na(it.customerStyleNo, "-")}</td>` : ""}
        ${hasSubRef ? `<td class="c muted">${na(it.subRef, "-")}</td>` : ""}
        <td>${esc(describe(it, pi.printLanguage))}${it.lotNumber ? `<br><span class="mono">LOT: ${esc(it.lotNumber)}</span>` : ""}</td>
        ${hasColor ? `<td class="c">${na(it.color, "-")}</td>` : ""}
        ${hasSize ? `<td class="c">${na(it.size, "-")}</td>` : ""}
        <td class="c b">${it.quantity}</td>
        <td class="c">${na(it.unit, "-")}</td>
        ${hasMoq ? `<td class="c">${it.moq != null ? it.moq : "-"}</td>` : ""}
        <td class="c b">${esc(pi.currency)} ${major4(unitShown)}</td>
        ${hasExw ? `<td class="c">${it.exwPrice != null ? `${esc(pi.currency)} ${major4(it.exwPrice)}` : "-"}</td>` : ""}
        ${hasDtd ? `<td class="c">${it.dtdPrice ? `${esc(pi.currency)} ${major4(it.dtdPrice)}` : "-"}</td>` : ""}
        ${hasUnderMoq ? `<td class="c">${it.underMoqPrice ? `${esc(pi.currency)} ${major4(it.underMoqPrice)}` : "-"}</td>` : ""}
        <td class="c b">${esc(pi.currency)} ${major4(it.amount)}</td>
      </tr>`;
    })
    .join("");

  const bankBlock = bank
    ? `<div style="margin-top:15px;">
        <div style="font-weight:bold;margin-bottom:10px;">PAYMENT INFORMATION:</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:15px;font-size:11px;">
          <div>
            <p><strong>Beneficiary:</strong> ${na(bank.beneficiary)}</p>
            <p><strong>Beneficiary Bank:</strong> ${na(bank.beneficiaryBank)}</p>
            <p><strong>Bank Address:</strong> ${na(bank.bankAddress)}</p>
          </div>
          <div>
            <p><strong>Account No (HKD):</strong> ${na(bank.accountNumberHkd)}</p>
            <p><strong>Account No (USD):</strong> ${na(bank.accountNumberUsd)}</p>
            ${
              // RMB chỉ in khi công ty có số: HKD/USD in "N/A" theo mẫu hệ cũ, còn RMB là
              // trường mới — in "N/A" trên mọi chứng từ của công ty không nhận RMB chỉ gây nhiễu.
              bank.accountNumberRmb?.trim() ? `<p><strong>Account No (RMB):</strong> ${na(bank.accountNumberRmb)}</p>` : ""
            }
            <p><strong>Swift Code:</strong> <span class="mono b">${na(bank.swiftCode)}</span></p>
          </div>
        </div>
      </div>`
    : "";

  const remarks = pi.remarks?.trim()
    ? `<div class="remarks-section">
        <div class="remarks-title">REMARKS:</div>
        <p style="margin:0;line-height:1.5;white-space:pre-wrap;">${esc(pi.remarks)}</p>
      </div>`
    : "";

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Proforma Invoice ${esc(pi.piNo)} - Hisense Button</title>
  <style>
    @page { margin: 15mm; size: A4; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, sans-serif; margin: 0; padding: 0; color: #000;
      line-height: 1.4; font-size: 12px; background: white;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .page { width: 100%; max-width: 210mm; margin: 0 auto; }
    .mono { font-family: "Courier New", monospace; }
    .c { text-align: center; } .b { font-weight: bold; } .muted { color: #666; }
    p { margin: 5px 0; }
    table.items { table-layout:fixed; overflow-wrap:anywhere; width: 100%; border-collapse: collapse; border: 2px solid #000; font-size: 11px; }
    table.items th, table.items td { border: 1px solid #000; padding: 5px 3px; vertical-align: top; }
    table.items th {
      background: #000 !important; color: #fff !important; font-weight: bold;
      padding: 6px 3px; font-size: 9px;
      -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
    }
    .remarks-section { margin: 20px 0; padding: 15px; border: 1px solid #000; }
    .remarks-title { font-weight: bold; margin-bottom: 10px; }
    .terms-section { margin: 20px 0; padding: 15px; border: 2px solid #000; }
    .terms-title { font-weight: bold; margin-bottom: 15px; font-size: 14px; }
    .remittance-notice { background: #f0f0f0; padding: 10px; margin: 10px 0; border-left: 4px solid #000; font-weight: bold; }
    @media print {
      body { margin: 0; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      table.items th { background: #000 !important; color: #fff !important; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:15px;">
      <div style="flex:1;">
        <img src="${esc(logoSrc)}" alt="Hisense Button Co., Ltd"
          style="max-height:80px;max-width:300px;object-fit:contain;"
          onerror="this.style.display='none';this.nextElementSibling.style.display='block';" />
        <div style="display:none;padding:15px;border:2px solid #000;max-width:300px;">
          <h2 style="margin:0;font-size:20px;">Hisense Button Co., Ltd</h2>
          <p style="margin:3px 0 0 0;font-size:12px;">Button Import &amp; Export Specialists</p>
        </div>
      </div>
      <div style="text-align:right;margin-left:20px;">
        <h1 style="margin:0;color:#2563eb;font-size:26px;font-weight:bold;letter-spacing:2px;">PROFORMA INVOICE</h1>
        <p style="margin:10px 0 2px;font-size:14px;">${pi.pProcess ? "<strong>P-Process</strong><br>" : ""}<strong>PI No:</strong> <span class="mono b">${esc(pi.piNo)}</span></p>
      </div>
    </div>

    <hr style="border:none;border-top:2px solid #000;margin:20px 0;">

    <div style="display:flex;gap:20px;margin-bottom:25px;">
      <div style="flex:0 0 60%;">
        <div style="display:flex;gap:20px;margin-bottom:20px;">
          <div style="flex:1;">
            <h3 class="box-label">BILL TO:</h3>
            <div class="box">${na(pi.billToName, "Not specified")}</div>
          </div>
          <div style="flex:1;">
            <h3 class="box-label">SHIPPING MARKS:</h3>
            <div class="box">${na(pi.shippingMarks, "Not specified")}</div>
          </div>
        </div>
        <h3 class="box-label">SHIP TO (Consignee):</h3>
        <div class="box">
          ${/* Không có người nhận riêng thì hàng giao cho chính người mua — đó là
                cách hệ cũ in, và 90/267 PI rơi vào trường hợp này. Bỏ trắng ô
                SHIP TO trên chứng từ thương mại là để hải quan và hãng vận
                chuyển không biết giao cho ai. Bản in HOÁ ĐƠN đã sửa đúng chỗ
                này từ trước (printInvoice.ts), riêng bản in PI thì quên. */""}
          ${(pi.consigneeName || pi.billToName) ? `<p style="margin:0 0 6px;font-weight:bold;font-size:13px;">${esc(pi.consigneeName || pi.billToName || "")}</p>` : ""}
          <p style="margin:0;white-space:pre-line;">${na(shipContact, "Not specified")}</p>
        </div>
      </div>
      <div style="flex:0 0 40%;border:1px solid #000;align-self:flex-start;font-size:9px;line-height:1.4;padding:8px 15px;">
        <p style="margin:0 0 5px;"><strong>Date:</strong> ${printDate(pi.date)}</p>
        <p><strong>Customer PO No:</strong> ${na(pi.customerPoNo)}</p>
        <p><strong>Customer Dept:</strong> <strong>${na(pi.customerDept)}</strong></p>
        <p><strong>Delivery Date:</strong> ${printDate(pi.deliveryDate)}</p>
        <p><strong>Shipping Mode:</strong> ${na(pi.shippingMode)}</p>
        <p><strong>Port of Loading:</strong> ${na(pi.portOfLoading)}</p>
        <p><strong>Country of Origin:</strong> ${na(pi.countryOfOrigin)}</p>
        <p><strong>D.N.No:</strong> ${na(pi.dnNo)}</p>
        <p><strong>Payment Terms:</strong> ${na(pi.paymentTerms)}</p>
        <p><strong>Currency:</strong> <strong>${esc(pi.currency)}</strong></p>
        <p style="margin:5px 0 0;"><strong>Resp. By:</strong> ${na(pi.respBy)}</p>
      </div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th>Product ID</th>
          ${hasStyle?"<th>Cust. Style No.</th>":""}
          ${hasSubRef?"<th>Sub Ref</th>":""}
          <th style="text-align:left;">Description</th>
          ${hasColor?"<th>Color</th>":""}
          ${hasSize?"<th>Size</th>":""}
          <th>Quantity</th>
          <th>Unit</th>
          ${hasMoq?"<th>MOQ</th>":""}
          <th>${priceHeader}</th>
          ${hasExw ? "<th>EXW (reference)</th>" : ""}
          ${hasDtd ? "<th>DTD Price</th>" : ""}
          ${hasUnderMoq ? "<th>Under MOQ</th>" : ""}
          <th>Total Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
        <tr style="background:#f9f9f9;">
          <td colspan="${totalColSpan}" style="text-align:right;font-weight:bold;">SUBTOTAL:</td>
          <td class="c b">${esc(pi.currency)} ${major4(lineSubtotal)}</td>
        </tr>
        <tr style="background:#f9f9f9;">
          <td colspan="${totalColSpan}" style="text-align:right;font-weight:bold;">UPCHARGE:</td>
          <td class="c b">${esc(pi.currency)} ${major4(pi.upcharge ?? 0)}</td>
        </tr>
        <tr><td colspan="${totalColSpan}" style="text-align:right;">ROUNDED BASE:</td><td class="c b">${esc(pi.currency)} ${major2(base)}</td></tr>
        <tr style="background:#f9f9f9;">
          <td colspan="${totalColSpan}" style="text-align:right;font-weight:bold;">LESS (${discountPct.toFixed(2)}%):</td>
          <td class="c b">-${esc(pi.currency)} ${major2(less)}</td>
        </tr>
        <tr style="background:#f0f0f0;border-top:2px solid #000;">
          <td colspan="${totalColSpan}" style="text-align:right;font-weight:bold;font-size:14px;padding:12px 8px;">GRAND TOTAL:</td>
          <td class="c b" style="font-size:16px;background:white;padding:12px 8px;">${esc(pi.currency)} ${major2(grand)}</td>
        </tr>
      </tbody>
    </table>

    ${remarks}

    <div class="terms-section">
      <div class="terms-title">TERMS &amp; CONDITIONS:</div>
      <div class="remittance-notice">
        ⚠️ ALL REMITTANCE CHARGES FROM OUTSIDE OF HONG KONG IS NOT ALLOWED TO DEDUCT FROM OUR INVOICE AMOUNT
      </div>
      ${bankBlock}
    </div>

    <div style="text-align:center;padding-top:20px;border-top:1px solid #000;font-size:10px;">
      <p style="margin:0;font-style:italic;">Thank you for your business!</p>
      <p style="margin:5px 0 0;">Generated on ${esc(new Date().toLocaleString())} | Hisense Button Export Import Management System</p>
    </div>
  </div>
  <style>
    .box-label { margin:0 0 8px;font-size:14px;font-weight:bold;border-bottom:1px solid #000;padding-bottom:3px; }
    .box { border:1px solid #000;padding:12px;min-height:60px; }
  </style>
</body>
</html>`;

  return html;
}

export function printProformaInvoice(pi: PiDetailDto) { printHtml(buildPiHtml(pi)); }
